import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Save, Plus, Trash2, Languages } from 'lucide-react';

interface TranslationEntry {
  key: string;
  translations: {
    [lang: string]: string;
  };
}

const TranslationDashboard: React.FC = () => {
  const { i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [translations, setTranslations] = useState<TranslationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'nn', name: 'Nynorsk' },
    { code: 'ta', name: 'தமிழ்' }
  ];

  useEffect(() => {
    loadTranslations();
  }, []);

  const loadTranslations = async () => {
    try {
      setLoading(true);
      const allTranslations: TranslationEntry[] = [];
      
      // Load translations for each language
      for (const lang of languages) {
        const response = await fetch(`/locales/${lang.code}/common.json`);
        const data = await response.json();
        
        // Flatten nested objects into dot notation
        const flattenedTranslations = flattenObject(data);
        
        // Merge into allTranslations
        Object.entries(flattenedTranslations).forEach(([key, value]) => {
          const existing = allTranslations.find(t => t.key === key);
          if (existing) {
            existing.translations[lang.code] = value as string;
          } else {
            allTranslations.push({
              key,
              translations: { [lang.code]: value as string }
            });
          }
        });
      }
      
      setTranslations(allTranslations);
    } catch (err) {
      setError('Failed to load translations');
      console.error('Error loading translations:', err);
    } finally {
      setLoading(false);
    }
  };

  const flattenObject = (obj: any, prefix = ''): { [key: string]: string } => {
    return Object.keys(obj).reduce((acc: { [key: string]: string }, k) => {
      const pre = prefix.length ? prefix + '.' : '';
      if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
        Object.assign(acc, flattenObject(obj[k], pre + k));
      } else {
        acc[pre + k] = obj[k];
      }
      return acc;
    }, {});
  };

  const unflattenObject = (obj: { [key: string]: string }) => {
    const result: any = {};
    
    for (const key in obj) {
      const parts = key.split('.');
      let current = result;
      
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (i === parts.length - 1) {
          current[part] = obj[key];
        } else {
          current[part] = current[part] || {};
          current = current[part];
        }
      }
    }
    
    return result;
  };

  const handleTranslationChange = (key: string, lang: string, value: string) => {
    setTranslations(prev => 
      prev.map(t => 
        t.key === key 
          ? { ...t, translations: { ...t.translations, [lang]: value } }
          : t
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Group translations by language
      const translationsByLang = languages.reduce((acc, lang) => {
        acc[lang.code] = {};
        translations.forEach(t => {
          if (t.translations[lang.code]) {
            acc[lang.code][t.key] = t.translations[lang.code];
          }
        });
        return acc;
      }, {} as { [lang: string]: { [key: string]: string } });
      
      // Unflatten and save each language file
      for (const lang of languages) {
        const unflattened = unflattenObject(translationsByLang[lang.code]);
        
        // In a real application, you would save this to your backend
        console.log(`Saving ${lang.code} translations:`, unflattened);
      }
      
      // Reload translations in the application
      await i18n.reloadResources();
      
    } catch (err) {
      setError('Failed to save translations');
      console.error('Error saving translations:', err);
    } finally {
      setSaving(false);
    }
  };

  const filteredTranslations = translations.filter(t =>
    t.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    Object.values(t.translations).some(v => 
      v.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Translation Management</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search translations..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Translation Key
                </th>
                {languages.map(lang => (
                  <th key={lang.code} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {lang.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTranslations.map((entry) => (
                <tr key={entry.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {entry.key}
                    </div>
                  </td>
                  {languages.map(lang => (
                    <td key={lang.code} className="px-6 py-4">
                      <textarea
                        value={entry.translations[lang.code] || ''}
                        onChange={(e) => handleTranslationChange(entry.key, lang.code, e.target.value)}
                        className="w-full min-h-[60px] border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TranslationDashboard;